import { ExportContext, ExportSheet } from './types';
import { downloadBlob, formatExportTimestamp } from './exportUtils';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// BRG brand blue, reused from the AG Grid header styling across the app.
const HEADER_FILL = 'FF1A3BCC';
const LABEL_COLOR = 'FF555555';

const MIN_COL_WIDTH = 10;
const MAX_COL_WIDTH = 60;

/** Excel forbids these in sheet names, and caps the name at 31 characters. */
function safeSheetName(name: string): string {
  return name.replace(/[\\/*?:[\]]/g, ' ').slice(0, 31) || 'Sheet';
}

function autoWidth(header: string, values: unknown[]): number {
  const longest = values.reduce<number>((max, v) => {
    const len = v == null ? 0 : String(v).length;
    return len > max ? len : max;
  }, header.length);
  return Math.min(Math.max(longest + 2, MIN_COL_WIDTH), MAX_COL_WIDTH);
}

/**
 * Exports one or more sheets as a real .xlsx file.
 *
 * Each sheet is stamped with the report title, who exported it and when, the
 * role-based access scope, and the filters that were active — so the file is
 * self-describing once it leaves the app.
 *
 * ExcelJS is ~1MB, so it is loaded on demand when the user actually exports.
 */
export async function exportToExcel(ctx: ExportContext, sheets: ExportSheet[]): Promise<void> {
  const mod = await import('exceljs');
  // The browser build is published as UMD; interop shape differs by bundler.
  const ExcelJS = ((mod as unknown as { default?: unknown }).default ?? mod) as typeof import('exceljs');

  const workbook = new ExcelJS.Workbook();
  workbook.creator = ctx.exportedBy || 'BRG Portal';
  workbook.created = new Date();

  const timestamp = formatExportTimestamp();

  for (const sheet of sheets) {
    writeSheet(workbook.addWorksheet(safeSheetName(sheet.name)), ctx, sheet, timestamp);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], { type: XLSX_MIME }), `${ctx.fileName}.xlsx`);
}

type Worksheet = import('exceljs').Worksheet;

function writeSheet(
  ws: Worksheet,
  ctx: ExportContext,
  sheet: ExportSheet,
  timestamp: string
): void {
  const colCount = Math.max(sheet.columns.length, 2);
  let row = 1;

  const writeMergedLine = (text: string, bold: boolean, size: number, color?: string) => {
    ws.mergeCells(row, 1, row, colCount);
    const cell = ws.getCell(row, 1);
    cell.value = text;
    cell.font = { bold, size, color: color ? { argb: color } : undefined };
    cell.alignment = { vertical: 'middle' };
    row += 1;
  };

  const writeLabelValue = (label: string, value: string | number) => {
    const labelCell = ws.getCell(row, 1);
    labelCell.value = label;
    labelCell.font = { bold: true, size: 10, color: { argb: LABEL_COLOR } };
    const valueCell = ws.getCell(row, 2);
    valueCell.value = value;
    valueCell.font = { size: 10 };
    row += 1;
  };

  writeMergedLine(ctx.title, true, 14);
  writeMergedLine(
    ctx.exportedBy ? `Exported ${timestamp} by ${ctx.exportedBy}` : `Exported ${timestamp}`,
    false,
    10,
    LABEL_COLOR
  );
  writeMergedLine(`Access scope: ${ctx.scopeLabel}`, false, 10, LABEL_COLOR);
  row += 1;

  writeMergedLine('Filters applied', true, 11);
  if (ctx.filters.length === 0) {
    writeLabelValue('None', '');
  } else {
    for (const filter of ctx.filters) {
      writeLabelValue(filter.label, filter.value);
    }
  }
  row += 1;

  if (sheet.summary && sheet.summary.length > 0) {
    writeMergedLine(sheet.summaryTitle ?? 'Summary', true, 11);
    for (const item of sheet.summary) {
      writeLabelValue(item.label, item.value);
    }
    row += 1;
  }

  // ─── Table header ────────────────────────────────────────────────────────
  const headerRowNumber = row;
  sheet.columns.forEach((col, idx) => {
    const cell = ws.getCell(headerRowNumber, idx + 1);
    cell.value = col.header;
    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
  ws.getRow(headerRowNumber).height = 20;
  row += 1;

  // ─── Data rows ───────────────────────────────────────────────────────────
  for (const dataRow of sheet.rows) {
    sheet.columns.forEach((col, idx) => {
      const cell = ws.getCell(row, idx + 1);
      const value = dataRow[col.key];
      cell.value = value === undefined ? null : value;
      cell.font = { size: 10 };
      cell.alignment = { vertical: 'top', wrapText: false };
      if (col.numFmt) cell.numFmt = col.numFmt;
    });
    row += 1;
  }

  // ─── Column widths, filtering, frozen header ─────────────────────────────
  sheet.columns.forEach((col, idx) => {
    ws.getColumn(idx + 1).width =
      col.width ?? autoWidth(col.header, sheet.rows.map((r) => r[col.key]));
  });

  if (sheet.rows.length > 0) {
    ws.autoFilter = {
      from: { row: headerRowNumber, column: 1 },
      to: { row: headerRowNumber + sheet.rows.length, column: sheet.columns.length },
    };
  }
  ws.views = [{ state: 'frozen', ySplit: headerRowNumber }];
}
